import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Validators, FormBuilder, FormGroup, FormControl, AbstractControl } from '@angular/forms';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ReplaySubject, Subject } from 'rxjs';
import * as moment from 'moment-timezone';
import { CountdownTimerDateTime } from '../../../models/countdown-timer';

@Component({
  selector: 'lib-event-datetime',
  templateUrl: './event-datetime.component.html',
  styleUrls: ['./event-datetime.component.scss']
})
export class EventDatetimeComponent implements OnInit, OnChanges, OnDestroy {

  @Input() data: CountdownTimerDateTime;
  @Output() handleSubmit = new EventEmitter();
  @Output() handleFormValid = new EventEmitter();

  public frm: FormGroup;

  // timezone
  public timezones;
  public tzFilterCtrl: FormControl = new FormControl();
  public filteredTimezones: ReplaySubject<moment.MomentZone[]> = new ReplaySubject<moment.MomentZone[]>();

  private _destroy$: Subject<void>;

  constructor(private _fb: FormBuilder) {
    this._destroy$ = new Subject();
    this.timezones = moment.tz.names();
    this.filteredTimezones.next(this.timezones);
    this._createForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data && changes.data.currentValue) {
      this._formMapping(changes.data.currentValue);
    }
  }

  ngOnInit() {
    this._subscriptions();
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get f() {
    if (!this.frm) { return {}; }
    return this.frm.controls;
  }

  getErrorMessage(control: AbstractControl) {
    if (!control.errors) {
      return '';
    }

    if (control.errors.required) {
      return 'This field is required.';
    }

    return 'Invalid field';
  }

  private _createForm() {
    this.frm = this._fb.group({
      date: [new Date(), [Validators.required]],
      time: ['04:20', [Validators.required]],
      timezone: ['America/New_York', [Validators.required]]
    });
  }

  private _formMapping(data: CountdownTimerDateTime) {
    if (!data) {
      return;
    }

    const { date, time, timezone } = data;

    this.f.date.setValue(date);
    this.f.time.setValue(time);
    this.f.timezone.setValue(timezone);
  }

  private _subscriptions() {
    this
      .frm
      .valueChanges
      .pipe(
        debounceTime(100),
        takeUntil(this._destroy$)
      )
      .subscribe(() => {
        this._onUpdateFormValid();
      });

    // listen for search field value changes
    this.tzFilterCtrl.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this._destroy$)
      )
      .subscribe(() => {
        this._filterTimZones();
      });
  }

  private _onUpdateFormValid() {
    const isValid =
      this.frm.dirty &&
      this.frm.valid;

    if (isValid) {
      this._onSubmit();
    } else {
      this.handleSubmit.emit(null);
    }
  }

  private _onSubmit() {
    if (!this.frm.dirty) {
      return;
    }

    this.handleSubmit.emit({ ...this._composeData() });
  }

  private _composeData() {
    const { date, time, timezone } = this.frm.value;

    const payload = new CountdownTimerDateTime();

    payload.date = date;
    payload.time = time;
    payload.timezone = timezone;

    return payload;
  }

  protected _filterTimZones() {
    if (!this.timezones) {
      return;
    }

    // get the search keyword
    let search = this.tzFilterCtrl.value;
    if (!search) {
      this.filteredTimezones.next(this.timezones.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    // filter the tzs
    this.filteredTimezones.next(
      this.timezones.filter(tz => tz.toLowerCase().indexOf(search) > -1)
    );
  }
}
